/*
 * assets/premium-cart.js
 * One cart controller for every Premium Cart view on the page:
 *   [data-pdc-view="drawer"]  sections/premium-prescription-cart-drawer.liquid
 *   [data-pdc-view="page"]    sections/premium-cart-page.liquid  (/cart)
 *
 * - All cart mutations go through ONE serial queue, so a newer click can never
 *   be overwritten by an older response, whichever view it came from.
 * - Every Shopify response carries the HTML of EVERY view (Section Rendering
 *   API), so the drawer and the page are repainted from the same cart state.
 * - Prescription lines: quantity = active right boxes + active left boxes.
 *   Each save reads the live line from /cart.js and merges into its full
 *   property set (Shopify's /cart/change.js replaces properties wholesale).
 *
 * Public API (window.PremiumCart): open(trigger), close(), isOpen(),
 *   refresh(), add(formData, { trigger, source })
 */
(function () {
  'use strict';

  if (window.PremiumCart && window.PremiumCart.__installed) return;

  var DEBOUNCE_MS = 250;
  var REFRESH_ON_OPEN_AFTER_MS = 15000;
  var EYE_RE = {
    right: /^(right(\b|_)|od\b)/i,
    left: /^(left(\b|_)|os\b)/i
  };
  var CART_TRIGGERS = [
    '#cart-icon-bubble',
    '.header__icon--cart',
    '[aria-controls="cart-drawer"]',
    '[data-cart-drawer-toggle]',
    'a[href="/cart"]',
    'a[href$="/cart"]'
  ].join(',');

  var state = {
    queue: Promise.resolve(),
    pending: 0,
    timers: new Map(),       // line element -> debounce timeout
    latestSections: null,    // newest Section Rendering payload, painted when idle
    needsFullRefresh: false,
    lines: [],               // live cart lines: {key, quantity, variant_id, selling_plan, properties}
    previousFocus: null,
    lastSyncAt: Date.now(),
    reconciled: {},
    activeView: null,        // view the customer last interacted with (for messages)
    confirm: null            // {view, el, key, trigger}
  };

  /* ---------------- views ---------------- */
  function views() { return Array.prototype.slice.call(document.querySelectorAll('[data-pdc-view]')); }
  function viewOf(node) { return node && node.closest ? node.closest('[data-pdc-view]') : null; }
  function drawerEl() { return document.getElementById('pdc-cart-drawer'); }
  function panelEl() { var d = drawerEl(); return d ? d.querySelector('.pdc-panel') : null; }
  function isOpen() { var d = drawerEl(); return !!(d && d.classList.contains('pdc-cart--open')); }
  function isVisible(view) {
    return !!view && (view.getAttribute('data-pdc-view') !== 'drawer' || isOpen());
  }
  function messageView() {
    var v = state.activeView;
    if (v && document.contains(v) && isVisible(v)) return v;
    if (isOpen()) return drawerEl();
    return document.querySelector('[data-pdc-view="page"]') || drawerEl();
  }
  function focusHome(view) {
    if (!view) return null;
    return view.getAttribute('data-pdc-view') === 'drawer' ? panelEl() : view.querySelector('[data-pdc-focus-home]') || view;
  }

  /* ---------------- helpers ---------------- */
  function rootUrl() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }
  function sectionIds() {
    var ids = [];
    views().forEach(function (v) {
      var id = v.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids.slice(0, 5);
  }
  function safeJSON(text, fallback) {
    try { return text ? JSON.parse(text) : fallback; } catch (e) { return fallback; }
  }
  function sortedJSON(obj) {
    var out = {};
    Object.keys(obj || {}).sort().forEach(function (k) { out[k] = obj[k]; });
    return JSON.stringify(out);
  }
  function setText(root, sel, text) { var n = root.querySelector(sel); if (n) n.textContent = text; }
  function setAllText(root, sel, text) {
    Array.prototype.forEach.call(root.querySelectorAll(sel), function (n) { n.textContent = text; });
  }

  /* Shopify money format from the view's data-money-format (shop.money_format). */
  function formatMoney(cents, view) {
    var source = view || document.querySelector('[data-money-format]');
    var fmt = (source && source.getAttribute('data-money-format')) || '{{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function delimit(number, precision, thousands, decimal) {
      if (number == null || isNaN(number)) return '0';
      var fixed = (number / 100).toFixed(precision).split('.');
      return fixed[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (fixed[1] ? decimal + fixed[1] : '');
    }
    var m = fmt.match(re);
    if (!m) return fmt;
    var value;
    switch (m[1]) {
      case 'amount_no_decimals': value = delimit(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimit(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = delimit(cents, 2, ' ', ','); break;
      default: value = delimit(cents, 2, ',', '.');
    }
    return fmt.replace(re, value).replace(/<[^>]*>/g, '');
  }

  /* ---------------- network ---------------- */
  function parseResponse(res) {
    return res.text().then(function (text) {
      var data = safeJSON(text, null);
      if (!res.ok || (data && typeof data.status === 'number' && data.status >= 400)) {
        var msg = data && (typeof data.description === 'string' ? data.description : data.message);
        var err = new Error(msg || 'Something went wrong. Please try again.');
        err.status = res.status;
        throw err;
      }
      return data;
    });
  }
  function postJSON(path, body) {
    return fetch(rootUrl() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(parseResponse);
  }
  function getCart() {
    return fetch(rootUrl() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(parseResponse);
  }
  function fetchSections() {
    var u = window.location.pathname + '?sections=' + encodeURIComponent(sectionIds().join(','));
    return fetch(u, { credentials: 'same-origin' }).then(parseResponse);
  }


  function setLinesFromCart(cart) {
    state.lines = (cart && cart.items ? cart.items : []).map(function (it) {
      return {
        key: it.key,
        quantity: it.quantity,
        variant_id: String(it.variant_id),
        selling_plan: it.selling_plan_allocation && it.selling_plan_allocation.selling_plan
          ? String(it.selling_plan_allocation.selling_plan.id) : '',
        properties: it.properties || {}
      };
    });
  }
  function lineIndex(key) {
    for (var i = 0; i < state.lines.length; i++) if (state.lines[i].key === key) return i;
    return -1;
  }

  /* ---------------- UI state ---------------- */
  function setBusy(on) {
    views().forEach(function (v) { v.classList.toggle('pdc--busy', !!on); });
  }
  function announce(text, view) {
    var v = view || messageView();
    var live = v && v.querySelector('[data-pdc-live]');
    if (!live) return;
    live.textContent = '';
    window.requestAnimationFrame(function () { live.textContent = text; });
  }
  function showAlert(message, view) {
    var v = view || messageView();
    var box = v && v.querySelector('[data-pdc-alert]');
    if (!box) return;
    box.querySelector('[data-pdc-alert-text]').textContent = message;
    box.hidden = false;
  }
  function hideAlerts() {
    views().forEach(function (v) {
      var box = v.querySelector('[data-pdc-alert]');
      if (box) box.hidden = true;
    });
  }

  function open(trigger) {
    var d = drawerEl();
    if (!d) return;
    if (!isOpen()) {
      state.previousFocus = trigger || document.activeElement;
      d.removeAttribute('inert');
      d.setAttribute('aria-hidden', 'false');
      d.classList.add('pdc-cart--open');
      document.documentElement.classList.add('pdc-cart-open');
      document.body.classList.add('pdc-cart-open');
      window.requestAnimationFrame(function () {
        var panel = panelEl();
        if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
      });
    }
    scheduleReconcile();
    if (!state.pending && Date.now() - state.lastSyncAt > REFRESH_ON_OPEN_AFTER_MS) refresh();
  }

  function close() {
    var d = drawerEl();
    if (!d || !isOpen()) return;
    if (state.confirm && state.confirm.view === d) closeConfirm(false);
    d.classList.remove('pdc-cart--open');
    d.setAttribute('aria-hidden', 'true');
    d.setAttribute('inert', '');
    document.documentElement.classList.remove('pdc-cart-open');
    document.body.classList.remove('pdc-cart-open');
    var prev = state.previousFocus;
    state.previousFocus = null;
    if (prev && typeof prev.focus === 'function' && document.contains(prev)) prev.focus({ preventScroll: true });
  }

  /* ---------------- rendering ---------------- */
  function describeFocus(view) {
    var a = document.activeElement;
    if (!a || !view.contains(a)) return null;
    var item = a.closest('[data-line-number]');
    var attrs = ['data-pdc-box-plus', 'data-pdc-box-minus', 'data-pdc-plus', 'data-pdc-minus', 'data-pdc-remove'];
    for (var i = 0; i < attrs.length; i++) {
      if (a.hasAttribute(attrs[i])) {
        var v = a.getAttribute(attrs[i]);
        return {
          line: item ? item.getAttribute('data-line-number') : null,
          sel: v ? '[' + attrs[i] + '="' + v + '"]' : '[' + attrs[i] + ']'
        };
      }
    }
    return { line: null, sel: null };
  }

  function restoreFocus(view, info) {
    if (!info || !isVisible(view)) return;
    var target = null;
    if (info.line && info.sel) {
      target = view.querySelector('[data-line-number="' + info.line + '"] ' + info.sel);
      if (target && target.disabled) {
        var row = target.closest('.pdc-step');
        target = row ? row.querySelector('button:not([disabled])') : null;
      }
    }
    if (!target) target = view.querySelector('[data-pdc-body] .pdc-remove, [data-pdc-empty] .pdc-btn');
    target = target || focusHome(view);
    if (target) target.focus({ preventScroll: true });
  }

  function paintBubble(html) {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var src = doc.querySelector('.shopify-section') || doc.body;
    if (src) bubble.innerHTML = src.innerHTML;
  }

  function paintView(view, html) {
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pdc-view="' + view.getAttribute('data-pdc-view') + '"]');
    var freshBody = fresh && fresh.querySelector('[data-pdc-body]');
    var body = view.querySelector('[data-pdc-body]');
    if (!freshBody || !body) return false;

    var focusInfo = describeFocus(view);
    var scroller = view.querySelector('[data-pdc-content]');
    var scrollTop = scroller ? scroller.scrollTop : 0;

    body.innerHTML = freshBody.innerHTML;
    var freshCount = fresh.querySelector('[data-pdc-count]');
    if (freshCount) setAllText(view, '[data-pdc-count]', freshCount.textContent);

    var newScroller = view.querySelector('[data-pdc-content]');
    if (newScroller) newScroller.scrollTop = scrollTop;
    applyStockLimits(view);
    restoreFocus(view, focusInfo);
    return true;
  }

  function paint(sections) {
    if (!sections) return false;
    if (sections['cart-icon-bubble']) paintBubble(sections['cart-icon-bubble']);
    var all = true;
    views().forEach(function (v) {
      var html = sections[v.getAttribute('data-section-id')];
      if (!html || !paintView(v, html)) all = false;
    });
    state.lastSyncAt = Date.now();
    scheduleReconcile();
    return all;
  }

  /* ---------------- request queue ---------------- */
  function enqueue(task) {
    state.pending++;
    setBusy(true);
    var run = state.queue.then(task);
    state.queue = run.catch(function () {}).then(function () {
      state.pending--;
      settle();
    });
    return run;
  }

  function settle() {
    if (state.pending || state.timers.size) return;
    if (state.needsFullRefresh) {
      state.needsFullRefresh = false;
      state.latestSections = null;
      enqueue(function () { return fetchSections().then(paint); }).catch(function (e) {
        console.error('[Premium Cart] refresh failed', e);
      });
      return;
    }
    if (state.latestSections) {
      var s = state.latestSections;
      state.latestSections = null;
      if (!paint(s)) { state.needsFullRefresh = true; settle(); return; }
    }
    views().forEach(function (v) {
      Array.prototype.forEach.call(v.querySelectorAll('.is-syncing'), function (n) { n.classList.remove('is-syncing'); });
    });
    setBusy(false);
  }

  function handleError(err, view) {
    console.error('[Premium Cart]', err);
    showAlert((err && err.message) || 'We could not update your cart. Please try again.', view);
    announce('Cart update failed', view);
    state.needsFullRefresh = true; /* repaint the real server state */
  }

  /* Dawn declares PUB_SUB_EVENTS with a top-level `const` (constants.js), so it
     is a global binding but NOT a window property. Read it by name. */
  function pubSubEvents() {
    try {
      /* global PUB_SUB_EVENTS */
      return typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    } catch (e) {
      return window.PUB_SUB_EVENTS;
    }
  }
  function publish(cart, extra) {
    var detail = Object.assign({ source: 'premium-cart', cartData: cart }, extra || {});
    var events = pubSubEvents();
    if (typeof window.publish === 'function' && events && events.cartUpdate) {
      window.publish(events.cartUpdate, detail);
    }
    document.dispatchEvent(new CustomEvent('pdc:cart-updated', { detail: detail }));
  }

  /* ---------------- prescription property maths ---------------- */
  function eyeBoxes(el, eye) {
    return Math.max(0, parseInt(el.getAttribute('data-' + eye + '-boxes'), 10) || 0);
  }
  function maxBoxes(el) {
    var v = viewOf(el);
    return parseInt(v && v.getAttribute('data-max-boxes'), 10) || 10;
  }

  /* ---------------- stock limits ----------------
     data-stock = units of the variant Shopify allows in the cart (rendered only
     when Shopify tracks inventory and overselling is off). Other lines of the
     same variant (e.g. two prescriptions of one lens) share that stock.
     This only prevents pointless requests; Shopify's response is final. */
  function stockLimit(el) {
    var raw = el.getAttribute('data-stock');
    if (raw === null || raw === '') return Infinity;
    var view = viewOf(el), variant = el.getAttribute('data-variant-id'), others = 0;
    Array.prototype.forEach.call(view.querySelectorAll('[data-pdc-line]:not([data-removing])'), function (o) {
      if (o !== el && o.getAttribute('data-variant-id') === variant) others += parseInt(o.getAttribute('data-shown-qty'), 10) || 0;
    });
    return Math.max(0, (parseInt(raw, 10) || 0) - others);
  }
  function stockMessage(el) {
    var total = parseInt(el.getAttribute('data-stock'), 10) || 0;
    return total < 1
      ? 'This item is out of stock — no more can be added.'
      : 'Only ' + total + ' in stock — you already have the maximum in your cart.';
  }
  function variantQty(view, variantId) {
    var n = 0;
    if (!view) return n;
    Array.prototype.forEach.call(view.querySelectorAll('[data-pdc-line]:not([data-removing])'), function (o) {
      if (o.getAttribute('data-variant-id') === String(variantId)) n += parseInt(o.getAttribute('data-shown-qty'), 10) || 0;
    });
    return n;
  }
  function setStockNote(el, text) {
    var n = el.querySelector('[data-pdc-stock-note]');
    if (!n) return;
    n.textContent = text || '';
    n.hidden = !text;
  }
  function applyStockLimits(view) {
    if (!view) return;
    Array.prototype.forEach.call(view.querySelectorAll('[data-pdc-line][data-stock]:not([data-removing])'), function (el) {
      var atLimit = (parseInt(el.getAttribute('data-shown-qty'), 10) || 0) >= stockLimit(el);
      Array.prototype.forEach.call(el.querySelectorAll('[data-pdc-plus],[data-pdc-box-plus]'), function (b) {
        if (atLimit) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
      });
    });
  }

  /* Builds the new properties from the LIVE cart line. Shopify's
     /cart/change.js REPLACES the whole properties object, so every key we do
     not deliberately change (SPH, CYL, AXIS, ADD, power_type, Recharge keys,
     anything else) is copied through untouched. An eye at 0 boxes is removed
     completely; legacy "_off:" markers from an earlier version are dropped. */
  function buildRxProperties(base, el) {
    var props = {};
    Object.keys(base || {}).forEach(function (k) {
      if (k.indexOf('_off:') === 0) return;
      props[k] = base[k];
    });
    ['right', 'left'].forEach(function (eye) {
      var n = eyeBoxes(el, eye);
      var boxKey = el.getAttribute('data-' + eye + '-key') || eye + '_eye_boxes';
      if (n > 0) {
        props[boxKey] = String(n);
        return;
      }
      Object.keys(props).forEach(function (k) {
        if (k.charAt(0) !== '_' && EYE_RE[eye].test(k)) delete props[k];
      });
    });
    return props;
  }

  /* Safety net: a key may only disappear if it belongs to an eye that is now
     at 0 boxes (or is a legacy "_off:" marker). Anything else aborts the save. */
  function unexpectedLoss(base, next, el) {
    return Object.keys(base || {}).filter(function (k) {
      if (Object.prototype.hasOwnProperty.call(next, k)) return false;
      if (k.indexOf('_off:') === 0) return false;
      if (EYE_RE.right.test(k) && eyeBoxes(el, 'right') === 0) return false;
      if (EYE_RE.left.test(k) && eyeBoxes(el, 'left') === 0) return false;
      return true;
    });
  }

  function desiredChange(el, line) {
    if (el.hasAttribute('data-removing')) return { quantity: 0 };
    if (el.hasAttribute('data-pdc-rx')) {
      var qty = eyeBoxes(el, 'right') + eyeBoxes(el, 'left');
      if (qty < 1) return { quantity: 0 };
      var props = buildRxProperties(line.properties, el);
      if (unexpectedLoss(line.properties, props, el).length) {
        throw new Error('Your prescription could not be updated safely. Please refresh and try again.');
      }
      var changed = sortedJSON(props) !== sortedJSON(line.properties);
      if (qty === line.quantity && !changed) return null;
      return { quantity: qty, properties: changed ? props : null };
    }
    var want = parseInt(el.getAttribute('data-shown-qty'), 10) || 0;
    return want === line.quantity ? null : { quantity: want };
  }

  /* Runs inside the serial queue. Always reads the live cart first, finds the
     line by its current KEY (never a cached index), merges, then writes. */
  function commitLine(el) {
    var key = el.getAttribute('data-pdc-line');
    return getCart().then(function (live) {
      setLinesFromCart(live);
      var idx = lineIndex(key);
      if (idx < 0) { state.needsFullRefresh = true; return; }
      var line = state.lines[idx];
      var change = desiredChange(el, line);
      if (!change) return;

      var body = {
        line: idx + 1,
        quantity: change.quantity,
        sections: sectionIds(),
        sections_url: window.location.pathname
      };
      if (change.properties) {
        body.properties = change.properties;
        if (line.selling_plan) body.selling_plan = Number(line.selling_plan);
      }

      return postJSON('cart/change.js', body).catch(function (err) {
        return stockRejected(err, el, key, idx, line, change);
      }).then(function (cart) {
        setLinesFromCart(cart);
        state.latestSections = cart.sections || null;
        if (!state.latestSections) state.needsFullRefresh = true;

        /* Shopify accepted less than requested (stock limit): show its quantity. */
        var got = cart.items[idx];
        if (change.quantity > 0 && got && String(got.variant_id) === line.variant_id && got.quantity < change.quantity) {
          return stockCapped(el, idx, line, got.quantity);
        }

        if (change.quantity === 0) {
          el.remove();
        } else {
          var next = cart.items[idx];
          var sameLine = next && String(next.variant_id) === line.variant_id &&
            (!change.properties || sortedJSON(next.properties) === sortedJSON(change.properties));
          if (sameLine) {
            el.setAttribute('data-pdc-line', next.key);
            el.setAttribute('data-qty', next.quantity);
          } else {
            state.needsFullRefresh = true; /* Shopify merged/reordered lines: repaint from server */
          }
        }
        publish(cart);
      });
    });
  }

  function availabilityText(el, accepted) {
    var title = el.getAttribute('aria-label') || 'this item';
    return 'Only ' + accepted + ' of ' + title + (accepted === 1 ? ' is' : ' are') + ' available. Your cart has been updated.';
  }

  /* A prescription line whose quantity was capped no longer matches its box
     properties, so put the line back to its last consistent state (previous
     boxes + previous quantity, same selling plan). Ordinary lines simply keep
     the quantity Shopify accepted. */
  function restoreLine(idx, line) {
    var body = {
      line: idx + 1,
      quantity: line.quantity,
      properties: line.properties,
      sections: sectionIds(),
      sections_url: window.location.pathname
    };
    if (line.selling_plan) body.selling_plan = Number(line.selling_plan);
    return postJSON('cart/change.js', body).then(function (cart) {
      setLinesFromCart(cart);
      state.latestSections = cart.sections || null;
      if (!state.latestSections) state.needsFullRefresh = true;
      return cart;
    });
  }

  /* 200 response, but Shopify kept fewer than requested. */
  function stockCapped(el, idx, line, accepted) {
    var view = viewOf(el);
    var done = el.hasAttribute('data-pdc-rx') ? restoreLine(idx, line) : Promise.resolve(null);
    return done.then(function (cart) {
      if (cart) publish(cart);
      var msg = el.hasAttribute('data-pdc-rx')
        ? 'Not enough stock for that many boxes. Your previous selection has been kept.'
        : availabilityText(el, accepted);
      showAlert(msg, view);
      announce(msg, view);
    });
  }

  /* 422 response. Shopify may still have applied part of the change (e.g.
     "Only 1 item was added to your cart due to availability."), so read the
     live cart, repair a prescription line if needed, then report. */
  function stockRejected(err, el, key, idx, line, change) {
    if (!err || err.status !== 422) throw err;
    return getCart().then(function (live) {
      setLinesFromCart(live);
      var i = lineIndex(key);
      if (i < 0 && live.items[idx] && String(live.items[idx].variant_id) === line.variant_id) i = idx;
      var cur = i > -1 ? live.items[i] : null;
      var touched = cur && (cur.quantity !== line.quantity || sortedJSON(cur.properties) !== sortedJSON(line.properties));
      var repair = el.hasAttribute('data-pdc-rx') && touched ? restoreLine(i, line).catch(function () {}) : Promise.resolve();
      return repair.then(function () {
        var msg = err.message;
        if (!el.hasAttribute('data-pdc-rx') && cur && touched && cur.quantity < change.quantity) msg = availabilityText(el, cur.quantity);
        if (el.hasAttribute('data-pdc-rx') && touched) msg = 'Not enough stock for that many boxes. Your previous selection has been kept.';
        var e = new Error(msg);
        e.status = 422;
        throw e;
      });
    });
  }

  function queueCommit(el) {
    var view = viewOf(el);
    return enqueue(function () {
      return commitLine(el).catch(function (e) { handleError(e, view); });
    });
  }

  function scheduleCommit(el) {
    clearTimeout(state.timers.get(el));
    el.classList.add('is-syncing');
    setBusy(true);
    state.timers.set(el, setTimeout(function () {
      state.timers.delete(el);
      queueCommit(el);
    }, DEBOUNCE_MS));
  }

  function flushTimers() {
    state.timers.forEach(function (t, el) {
      clearTimeout(t);
      queueCommit(el);
    });
    state.timers.clear();
  }

  /* Persist lines whose quantity did not match their boxes (see snippet).
     Only for views the customer can currently see. */
  function scheduleReconcile() {
    views().forEach(function (v) {
      if (!isVisible(v)) return;
      Array.prototype.forEach.call(v.querySelectorAll('[data-pdc-needs-sync]'), function (el) {
        var key = el.getAttribute('data-pdc-line');
        if (state.reconciled[key] || el.hasAttribute('data-locked')) return;
        state.reconciled[key] = true;
        el.removeAttribute('data-pdc-needs-sync');
        queueCommit(el);
      });
    });
  }

  /* ---------------- optimistic UI (per view) ---------------- */
  function adjustTotals(view, deltaCents) {
    var current = 0;
    Array.prototype.forEach.call(view.querySelectorAll('[data-pdc-line]:not([data-removing])'), function (el) {
      current += parseInt(el.getAttribute('data-shown-qty'), 10) || 0;
    });
    var label = current === 1 ? '1 item' : current + ' items';
    setAllText(view, '[data-pdc-count]', label);
    setAllText(view, '[data-pdc-footer-count]', label);
    if (deltaCents) {
      Array.prototype.forEach.call(view.querySelectorAll('[data-pdc-subtotal]'), function (sub) {
        var cents = (parseInt(sub.getAttribute('data-cents'), 10) || 0) + deltaCents;
        sub.setAttribute('data-cents', cents);
        sub.textContent = formatMoney(cents, view);
      });
    }
    var empty = current === 0;
    var emptyEl = view.querySelector('[data-pdc-empty]');
    if (emptyEl) emptyEl.hidden = !empty;
    Array.prototype.forEach.call(view.querySelectorAll('[data-pdc-hide-when-empty]'), function (n) { n.hidden = empty; });
  }

  function applyLineQuantity(el, qty) {
    var view = viewOf(el);
    var prev = parseInt(el.getAttribute('data-shown-qty'), 10) || 0;
    var unit = parseInt(el.getAttribute('data-unit-final'), 10) || 0;
    var unitOriginal = parseInt(el.getAttribute('data-unit-original'), 10) || unit;
    el.setAttribute('data-shown-qty', qty);
    setText(el, '[data-pdc-line-price]', formatMoney(unit * qty, view));
    setText(el, '[data-pdc-line-compare]', formatMoney(unitOriginal * qty, view));
    var note = el.querySelector('[data-pdc-unit-note]');
    if (note) note.hidden = qty < 2;
    var word = el.querySelector('[data-pdc-box-word]');
    if (word) word.textContent = qty + (qty === 1 ? ' box' : ' boxes');
    adjustTotals(view, (qty - prev) * unit);
    applyStockLimits(view);
  }

  function updateRxUI(el) {
    var max = maxBoxes(el);
    var r = eyeBoxes(el, 'right');
    var l = eyeBoxes(el, 'left');
    var total = r + l;
    var locked = el.hasAttribute('data-locked');
    [['right', r], ['left', l]].forEach(function (pair) {
      var eye = pair[0], n = pair[1];
      var row = el.querySelector('[data-pdc-eye="' + eye + '"]');
      if (!row) return;
      row.hidden = n < 1;
      setText(row, '[data-pdc-box-count]', String(n));
      var minus = row.querySelector('[data-pdc-box-minus]');
      var plus = row.querySelector('[data-pdc-box-plus]');
      if (minus) minus.disabled = locked || n < 1;
      if (plus) plus.disabled = locked || n >= max;
    });
    var summary = el.querySelector('[data-pdc-box-summary]');
    if (summary) summary.hidden = !(r > 0 && l > 0);
    setText(el, '[data-pdc-box-eq]', r + ' + ' + l + ' =');
    setText(el, '[data-pdc-box-total]', String(total));
    applyLineQuantity(el, total);
  }

  function onBoxButton(btn) {
    var el = btn.closest('[data-pdc-line]');
    if (!el || el.hasAttribute('data-removing') || el.hasAttribute('data-locked')) return;
    var eye = btn.getAttribute('data-pdc-box-plus') || btn.getAttribute('data-pdc-box-minus');
    if (eye !== 'right' && eye !== 'left') return;
    var step = btn.hasAttribute('data-pdc-box-plus') ? 1 : -1;
    var current = eyeBoxes(el, eye);
    var other = eyeBoxes(el, eye === 'right' ? 'left' : 'right');
    var next = current + step;
    if (next < 0 || next > maxBoxes(el)) return;

    /* Last box of the last active eye = removing the whole line: confirm first. */
    if (next + other < 1) { openConfirm(el, btn); return; }

    if (step > 0) {
      var limitB = stockLimit(el);
      if (next + other > limitB) {
        setStockNote(el, stockMessage(el));
        announce(stockMessage(el), viewOf(el));
        return;
      }
    }
    setStockNote(el, '');

    el.setAttribute('data-' + eye + '-boxes', next);
    updateRxUI(el);
    var name = eye === 'right' ? 'Right' : 'Left';
    if (next === 0) {
      announce(name + ' eye removed. Total ' + other + (other === 1 ? ' box.' : ' boxes.'), viewOf(el));
      var focusTarget = el.querySelector('[data-pdc-eye]:not([hidden]) [data-pdc-box-minus]') || el.querySelector('[data-pdc-remove]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    } else {
      announce(name + ' eye: ' + next + (next === 1 ? ' box' : ' boxes') +
        '. Total ' + (next + other) + (next + other === 1 ? ' box.' : ' boxes.'), viewOf(el));
    }
    scheduleCommit(el);
  }

  function onQtyButton(btn) {
    var el = btn.closest('[data-pdc-line]');
    if (!el || el.hasAttribute('data-removing') || el.hasAttribute('data-locked')) return;
    var current = parseInt(el.getAttribute('data-shown-qty'), 10) || 1;
    var next = btn.hasAttribute('data-pdc-plus') ? current + 1 : Math.max(1, current - 1);
    if (next === current) return;
    if (next > current) {
      var limitQ = stockLimit(el);
      if (next > limitQ) {
        setStockNote(el, stockMessage(el));
        announce(stockMessage(el), viewOf(el));
        return;
      }
    }
    setStockNote(el, '');
    setText(el, '[data-pdc-qty]', String(next));
    var minus = el.querySelector('[data-pdc-minus]');
    if (minus) minus.disabled = next <= 1;
    applyLineQuantity(el, next);
    announce('Quantity ' + next, viewOf(el));
    scheduleCommit(el);
  }

  function findLine(view, key) {
    if (!view || !key) return null;
    var all = view.querySelectorAll('[data-pdc-line]');
    for (var i = 0; i < all.length; i++) if (all[i].getAttribute('data-pdc-line') === key) return all[i];
    return null;
  }

  function doRemove(el) {
    if (!el || el.hasAttribute('data-removing')) return;
    var view = viewOf(el);
    clearTimeout(state.timers.get(el));
    state.timers.delete(el);
    var shown = parseInt(el.getAttribute('data-shown-qty'), 10) || 0;
    var unit = parseInt(el.getAttribute('data-unit-final'), 10) || 0;
    el.setAttribute('data-removing', '');
    el.hidden = true;
    adjustTotals(view, -shown * unit);
    announce((el.getAttribute('aria-label') || 'Item') + ' removed from cart', view);
    var nextFocus = view.querySelector('[data-pdc-line]:not([data-removing]) .pdc-remove') ||
      view.querySelector('[data-pdc-empty] .pdc-btn') || focusHome(view);
    if (nextFocus) nextFocus.focus({ preventScroll: true });
    queueCommit(el);
  }

  /* ---------------- remove confirmation (one per view) ---------------- */
  function confirmOf(view) { return view ? view.querySelector('[data-pdc-confirm]') : null; }
  function isConfirmOpen() { return !!state.confirm; }

  function openConfirm(el, trigger) {
    var view = viewOf(el);
    var c = confirmOf(view);
    if (!c || !el || el.hasAttribute('data-removing')) return;
    state.confirm = { view: view, el: el, key: el.getAttribute('data-pdc-line'), trigger: trigger };
    c.querySelector('[data-pdc-confirm-name]').textContent = el.getAttribute('aria-label') || '';
    c.querySelector('[data-pdc-confirm-ok]').disabled = false;
    c.hidden = false;
    if (view.getAttribute('data-pdc-view') === 'page') document.documentElement.classList.add('pdc-confirm-open');
    var keep = c.querySelector('.pdc-confirm__keep');
    if (keep) keep.focus({ preventScroll: true });
  }

  function closeConfirm(returnFocus) {
    var s = state.confirm;
    if (!s) return;
    state.confirm = null;
    var c = confirmOf(s.view);
    if (c) c.hidden = true;
    document.documentElement.classList.remove('pdc-confirm-open');
    if (!returnFocus) return;
    var target = s.trigger && document.contains(s.trigger) ? s.trigger : null;
    if (!target) { var line = findLine(s.view, s.key); target = line && line.querySelector('[data-pdc-remove]'); }
    target = target || focusHome(s.view);
    if (target) target.focus({ preventScroll: true });
  }

  function confirmRemove() {
    var s = state.confirm;
    if (!s) return;
    var ok = confirmOf(s.view).querySelector('[data-pdc-confirm-ok]');
    if (ok.disabled) return; /* guards against double clicks */
    ok.disabled = true;
    var el = s.el && document.contains(s.el) ? s.el : findLine(s.view, s.key);
    closeConfirm(false);
    if (el) doRemove(el); else refresh();
  }

  /* ---------------- public operations ---------------- */
  function refresh() {
    flushTimers();
    return enqueue(function () {
      return fetchSections().then(function (sections) {
        state.latestSections = null;
        state.needsFullRefresh = false;
        paint(sections);
      });
    }).catch(function (e) { console.error('[Premium Cart] refresh failed', e); });
  }

  /* One add request per call. Resolves with Shopify's /cart/add.js payload. */
  function add(formData, options) {
    options = options || {};
    flushTimers();
    hideAlerts();
    formData.delete('return_to');
    formData.set('sections', sectionIds().join(','));
    formData.set('sections_url', window.location.pathname);

    return enqueue(function () {
      return fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      }).then(parseResponse).then(function (data) {
        state.latestSections = null;
        var variantId = formData.get('id');
        var requested = parseInt(formData.get('quantity'), 10) || 1;
        var before = variantQty(drawerEl(), variantId);
        if (!paint(data && data.sections)) state.needsFullRefresh = true;
        var added = variantQty(drawerEl(), variantId) - before;
        if (data && data.sections && added >= 0 && added < requested) {
          /* Shopify accepted fewer than requested without an error. */
          var t = data.product_title || data.title || 'this item';
          showAlert(added === 0
            ? 'No more ' + t + ' could be added — it is out of stock.'
            : 'Only ' + added + ' of ' + t + ' could be added due to availability. Your cart has been updated.', drawerEl());
        }
        open(options.trigger);
        state.activeView = drawerEl();
        announce('Added to cart', drawerEl());
        publish(null, { productVariantId: formData.get('id'), addResponse: data, origin: options.source || 'premium-cart' });
        return data;
      }, function (err) {
        /* Shopify can add PART of the quantity and still answer 422
           ("Only 1 item was added to your cart due to availability."):
           always repaint the real cart after a failed add. */
        state.needsFullRefresh = true;
        throw err;
      });
    }).catch(function (err) {
      showAlert(err.message || 'Unable to add this item. Please try again.', drawerEl());
      open(options.trigger);
      throw err;
    });
  }

  window.PremiumCart = {
    __installed: true,
    open: open,
    close: close,
    isOpen: isOpen,
    refresh: refresh,
    add: add
  };

  /* ---------------- event wiring (document-level, once) ---------------- */
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!(t instanceof Element)) return;
    var view = viewOf(t);

    if (view) {
      state.activeView = view;
      if (t.closest('[data-pdc-confirm-ok]')) { event.preventDefault(); confirmRemove(); return; }
      if (t.closest('[data-pdc-confirm-cancel]')) { event.preventDefault(); closeConfirm(true); return; }
      if (isConfirmOpen()) return;
      if (t.closest('[data-pdc-close]')) { event.preventDefault(); close(); return; }
      if (t.closest('[data-pdc-alert-close]')) { hideAlerts(); return; }
      var b;
      if ((b = t.closest('[data-pdc-remove]'))) {
        event.preventDefault();
        var line = b.closest('[data-pdc-line]');
        if (line) openConfirm(line, b);
        return;
      }
      if ((b = t.closest('[data-pdc-box-plus],[data-pdc-box-minus]'))) { event.preventDefault(); onBoxButton(b); return; }
      if ((b = t.closest('[data-pdc-plus],[data-pdc-minus]'))) { event.preventDefault(); onQtyButton(b); return; }
      if (view === drawerEl()) return;
    }

    var opener = t.closest('[data-pdc-open-cart]');
    if (opener) { event.preventDefault(); open(opener); }
  });

  /* Header cart icon / any cart link -> premium drawer (capture phase, so
     legacy listeners on the icon never run). Links inside a cart view and
     links marked data-no-pdc keep their normal behaviour. */
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!(t instanceof Element) || !drawerEl()) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
    var trigger = t.closest(CART_TRIGGERS);
    if (!trigger || viewOf(trigger) || trigger.hasAttribute('data-no-pdc')) return;
    if (trigger.closest('form[action*="/cart/add"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(trigger);
  }, true);

  /* Add-to-cart forms. Capture phase on document so Dawn's product-form
     handler (which would open the legacy drawer) never runs. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !drawerEl()) return;
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add(?:\.js)?(?:\?|$)/.test(action)) return;
    if (form.hasAttribute('data-no-pdc-ajax')) return;

    /* Buy Now / Subscribe ("Proceed to pay") set return_to=/checkout and must
       reach Shopify natively, with the contact-lens + Recharge submit
       listeners intact. Do not intercept them. */
    var returnTo = form.querySelector('[name="return_to"]');
    if (returnTo && !returnTo.disabled && String(returnTo.value || '').trim()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    /* Contact-lens forms: route through the PDP Add to Cart handler so
       validation + eye/box quantity logic run exactly once. */
    var clRoot = form.closest('[data-basari-contact-lens]');
    if (clRoot) {
      var clButton = clRoot.querySelector('[data-basari-add-cart]');
      if (clButton && !clButton.disabled) clButton.click();
      return;
    }

    var submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter && (submitter.disabled || submitter.getAttribute('aria-busy') === 'true')) return;
    if (submitter) { submitter.disabled = true; submitter.setAttribute('aria-busy', 'true'); }

    add(new FormData(form), { trigger: submitter }).catch(function () {}).then(function () {
      if (submitter && document.contains(submitter)) {
        submitter.disabled = false;
        submitter.removeAttribute('aria-busy');
      }
    });
  }, true);

  function trapTab(event, container, buttonsSelector) {
    var focusable = Array.prototype.filter.call(container.querySelectorAll(buttonsSelector), function (n) {
      return n.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0], last = focusable[focusable.length - 1];
    if (!container.contains(document.activeElement) || document.activeElement === container) {
      event.preventDefault(); (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }

  document.addEventListener('keydown', function (event) {
    if (state.confirm) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeConfirm(true); return; }
      if (event.key === 'Tab') trapTab(event, confirmOf(state.confirm.view).querySelector('.pdc-confirm__box'), '.pdc-confirm__actions button:not([disabled])');
      return;
    }
    if (!isOpen()) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key === 'Tab') {
      trapTab(event, panelEl(), 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])');
    }
  });

  document.addEventListener('pdc:open-cart', function (e) { open(e && e.detail && e.detail.trigger); });
  document.addEventListener('pdc:refresh', function () { refresh(); });
  document.addEventListener('cart:refresh', function () { refresh(); });

  /* Returning via back/forward cache (e.g. back from checkout): the cart may
     have changed elsewhere. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });

  function subscribeToTheme() {
    var events = pubSubEvents();
    if (typeof window.subscribe === 'function' && events && events.cartUpdate) {
      window.subscribe(events.cartUpdate, function (e) {
        if (e && e.source === 'premium-cart') return;
        /* Just repainted from a Shopify response (e.g. via the Dawn bridge): skip a redundant fetch. */
        if (Date.now() - state.lastSyncAt < 1500) return;
        refresh();
      });
    }
  }

  /* Theme editor */
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#pdc-cart-drawer')) open();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#pdc-cart-drawer')) close();
  });

  /* Dawn cart-drawer compatibility bridge.
     Theme scripts written for Dawn (e.g. assets/basari-buy-frame.js) look for
     document.querySelector('cart-drawer') and call getSectionsToRender() /
     renderContents(). When the theme's Cart type is not "Drawer", Dawn's
     <cart-drawer> is not rendered and those scripts abort with an error before
     adding anything. In that case we provide a hidden stand-in element that
     speaks the same API and hands everything to the Premium drawer.
     Runs at DOMContentLoaded, i.e. after every deferred theme script.

     Guard: only a real <cart-drawer> ELEMENT on the page means Dawn's drawer
     exists. assets/cart-drawer.js registers the cart-drawer custom element
     whenever Cart type = Drawer, even if the (custom) header never renders the
     element, so the registration alone must NOT stop the bridge. If the element
     type is registered, the bridge becomes a Dawn CartDrawer instance (our
     patched cart-drawer.js constructor tolerates the missing overlay), and the
     bridge's own methods below take precedence over Dawn's prototype ones. */
  function installDawnBridge() {
    if (!drawerEl()) return;
    if (document.querySelector('cart-drawer')) return;
    var bridge;
    try {
      bridge = document.createElement('cart-drawer');
    } catch (e) {
      console.warn('[Premium Cart] Dawn cart-drawer bridge not installed:', e);
      return;
    }
    bridge.setAttribute('data-pdc-bridge', '');
    bridge.setAttribute('aria-hidden', 'true');
    bridge.hidden = true;
    bridge.getSectionsToRender = function () {
      return sectionIds().map(function (id) { return { id: id, section: id, selector: '.shopify-section' }; });
    };
    bridge.setActiveElement = function (el) { bridge.activeElement = el; };
    bridge.renderContents = function (payload) {
      var trigger = bridge.activeElement || document.activeElement;
      if (payload && payload.sections && paint(payload.sections)) {
        open(trigger);
        state.activeView = drawerEl();
        announce('Added to cart', drawerEl());
      } else {
        refresh().then(function () { open(trigger); });
      }
    };
    bridge.open = function (trigger) { open(trigger); };
    bridge.close = function () { close(); };
    document.body.appendChild(bridge);
  }

  function init() {
    document.documentElement.classList.add('pdc-ready');
    /* Install the bridge only once every deferred theme script has run, so the
       decision sees the final page (Dawn drawer element present or not),
       whatever order the scripts load in. installDawnBridge() is idempotent. */
    if (document.readyState === 'complete') {
      installDawnBridge();
    } else {
      document.addEventListener('DOMContentLoaded', installDawnBridge, { once: true });
      window.addEventListener('load', installDawnBridge, { once: true });
    }
    subscribeToTheme();
    views().forEach(applyStockLimits);
    scheduleReconcile(); /* the cart page is visible on load */
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
